/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useState } from 'react';
import { Button, Menu } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AiTemplateMenuItem from '@/components/buttons/GenerateAITemplateMenuButton/AiTemplateMenuItem';
import EngageReportMenuItem from '@/components/buttons/GenerateAITemplateMenuButton/EngageReportMenuItem';

/**
 * @typedef {Object} GenerateMenuItem
 * @property {'aiWorkflow' | 'folioEngage'} workflowType - `aiWorkflow` renders an
 *   {@link AiTemplateMenuItem}; `folioEngage` renders an {@link EngageReportMenuItem}.
 * @property {string} type - For `aiWorkflow`, an `AI_EMAIL_WORKFLOW_TYPES` value (e.g.
 *   `riskInfoNBEmail`); for `folioEngage`, an `ENGAGE_REPORT_TYPES` key (e.g.
 *   `pre_renewal_report`).
 */

/**
 * The deal tab header's "Generate ..." button, with a dropdown menu of AI email
 * workflows and Folio Engage reports.
 *
 * @param {Object} props
 * @param {GenerateMenuItem[]} [props.items=[]] - Menu items, in display order.
 * @returns {JSX.Element}
 *
 * @example
 * <GenerateAITemplateMenuButton
 * 	items={[
 * 		{ workflowType: 'aiWorkflow', type: 'riskInfoNBEmail' },
 * 		{ workflowType: 'folioEngage', type: 'pre_renewal_report' },
 * 	]}
 * />
 */
export const GenerateAITemplateMenuButton = ({ items = [] }) => {
	const [menuAnchor, setMenuAnchor] = useState(null);
	const menuOpen = Boolean(menuAnchor);

	const handleOpenMenu = (event) => {
		setMenuAnchor(event.currentTarget);
	};

	const handleCloseMenu = () => {
		setMenuAnchor(null);
	};

	return (
		<>
			<Button
				variant='tab_header'
				startIcon={<AutoAwesomeIcon />}
				endIcon={<ArrowDropDownIcon />}
				onClick={handleOpenMenu}
			>
				Generate ...
			</Button>

			<Menu
				anchorEl={menuAnchor}
				open={menuOpen}
				onClose={handleCloseMenu}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
			>
				{items.map((item) =>
					item.workflowType === 'aiWorkflow' ? (
						<AiTemplateMenuItem
							key={item.type}
							workflowType={item.type}
							onCloseMenu={handleCloseMenu}
						/>
					) : (
						<EngageReportMenuItem
							key={item.type}
							reportType={item.type}
							onCloseMenu={handleCloseMenu}
						/>
					),
				)}
			</Menu>
		</>
	);
};
GenerateAITemplateMenuButton.displayName = 'GenerateAITemplateMenuButton';

export default GenerateAITemplateMenuButton;
